/**
 * /best-product-management-software (zh) — 中文变体。
 * 对标 "产品管理软件 / 产品管理工具" 关键词族,延续英文版"诚实清单/指南"基调:
 * 不是软广,而是按 PM 实际用途分类,大方点名各类里真正强的软件
 * (Productboard、Aha、Linear、Jira、Notion、Coda、ChatPRD),
 * 只把 Draftlize 放在它真正独占的那一类:让产品决策不漂移
 * (决策追踪 + stale 传播 + agent 可读写卡片)。绝不假装它做路线图或
 * issue 跟踪。诚实是这页可信度的关键 —— 呼应 comparisons/ChatGpt.tsx
 * 那种"大方承认对手长处"的调性。
 *
 * 英文变体见 BestProductManagementSoftware.tsx。
 */
import { MarketingShell } from './MarketingShell';

export function BestProductManagementSoftwareZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/best-product-management-software"
      documentTitle="产品管理软件清单(2026):按用途诚实排序 · Draftlize"
      documentDescription="一份不灌水的产品管理软件清单(2026),按用途分类:路线图、issue 跟踪、文档知识库、AI 写 PRD,以及让产品决策不漂移。每一类都点名真正最强的工具,而不是一堵返佣 logo 墙。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 860 }}>
          <span className="label label-amber">指南 · 产品管理软件</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.12, margin: '12px 0 24px' }}
          >
            产品管理软件清单,<br/>
            <em>按每款真正的用途来排。</em>
          </h1>
          <p className="lede">
            大多数"产品管理软件"榜单只是一堵 logo 墙,外加一个花钱买来的第一名。这篇不是。我们自己做其中一款工具,但我们会跟你直说:路线图、issue 跟踪、文档、写 PRD——每一类都有比我们更强、更出名的软件,我们会一个个点名。Draftlize 在这页只配得上一类:让你的产品决策不悄悄漂移、不互相打架。下面是整个品类的诚实地图。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">一句话</span>
            <h2 className="serif">没有哪一款全包。<em>按用途各选一个。</em></h2>
          </div>
          <p>常见的错是:想找一款"产品管理软件"通吃所有事。好答案是搭一套小栈——一个管路线图、一个跟踪工作、一个写文档、一个起草 spec,再加一层保证它们底下的决策不烂掉。下面是每一行里谁胜出,照实说。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>你要它干什么</th>
              <th>值得用的</th>
              <th className="ours">Draftlize 在哪一格</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>路线图 / 优先级</td><td className="yes">Productboard、Aha</td><td className="ours no">不是路线图工具</td></tr>
            <tr><td>issue / 项目跟踪</td><td className="yes">Linear、Jira</td><td className="ours no">不是 issue 跟踪器</td></tr>
            <tr><td>文档 / 知识库</td><td className="yes">Notion、Coda</td><td className="ours partial">只存决策,不是整个 wiki</td></tr>
            <tr><td>用 AI 写 PRD / spec</td><td className="yes">ChatPRD、Notion AI</td><td className="ours partial">存 PRD 引用的那些决策</td></tr>
            <tr><td>让决策不漂移</td><td className="partial">—— 几乎没人真正在做 ——</td><td className="ours yes">这就是整款产品</td></tr>
            <tr><td>改一个决策,自动标出哪些会塌</td><td className="no">上面没一个能</td><td className="ours yes">依赖图 + 自动 stale 标记</td></tr>
            <tr><td>Claude Code / Cursor 共享同一份上下文</td><td className="no">每个工具各记各的</td><td className="ours yes">一张卡片图,经 MCP 可读可写</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">按类别,<em>照实说。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">路线图与优先级</h3>
            <p>
              <strong>Productboard</strong>——这一类的标杆:收集反馈、给功能打分、把利益相关方都能看的路线图理出来。<br/>
              <strong>Aha</strong>——更重、更结构化,擅长把战略一路串到路线图,适合更大的组织。<br/>
              <em>实话:</em>这里决定的是<em>做什么</em>、按什么顺序做。Draftlize 不做路线图——要路线图,就用这些。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">issue 与项目跟踪</h3>
            <p>
              <strong>Linear</strong>——快、有主见,被产品驱动的团队钟爱,是跟踪工作的现代默认。<br/>
              <strong>Jira</strong>——企业主力:可配置、无处不在、集成极深。<br/>
              <em>实话:</em>这里住着"执行"——工单、迭代、状态。Draftlize 不是 issue 跟踪器,也不会假装是。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">文档与知识库</h3>
            <p>
              <strong>Notion</strong>——团队 wiki 的默认,灵活、可协作,大家手上都有。<br/>
              <strong>Coda</strong>——像应用一样的文档,内建表格和自动化。<br/>
              <em>实话:</em>存知识很好用。但一个文档不知道它记录的那条决策什么时候不再成立——它只会原地杵着,理直气壮地错下去。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">用 AI 写 PRD 与 spec</h3>
            <p>
              <strong>ChatPRD</strong>——专为 PRD 格式而生,带模板和"教练式"语气,是 AI 写作器里最"产品经理形状"的那个。<br/>
              <strong>Notion AI</strong>——如果你的文档本来就在 Notion,想就地起草,它够用。<br/>
              <em>实话:</em>它们能把空白页飞快变成草稿。但它们不会在三周后还记得:这份 spec 当初假设的那个价格,早就改了。
            </p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">唯一<em>真正属于我们</em>的那一类。</h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            让产品决策不漂移。这是一件很窄的事,也是我们唯一敢认的一类——它不是路线图,也不是 issue 跟踪。但上面没一个工具在做,而跳过它,正是一套整整齐齐的产品管理软件栈最后还是各说各话的原因。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">决策落成结构化卡片</h3>
            <p>一个路线图押注、一段文档文字、一份 PRD 草稿——<em>产物</em>都还行,但底下那条决策被埋进了大段文字和工单评论里。Draftlize 把它沉淀成一张卡片:决策、理由、备选、依赖。可按 URL 和 ID 寻址,于是"依据决策 c-04"是一个真链接,而不是模糊的记忆。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个,依赖它的自动标 stale</h3>
            <p>这正是任何路线图工具、issue 跟踪器、文档、PRD 写作器都不做的事。每张卡片都知道谁依赖它。改掉定价决策,所有建立在旧价格上的 spec、工单、文档都会自动变 stale——就像构建系统会让改动文件的所有下游全部失效。这就是我们说它让决策"不漂移"的意思。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 AI agent 能读也能写</h3>
            <p>用 ChatPRD 起草、Linear 跟踪、Claude Code 或 Cursor 落地——每个都各记各的状态。Draftlize 的卡片是它们共享的那一份上下文,经 MCP 可读<em>也</em>可写,于是给你起草下一份 spec 的 agent,早就知道整套栈所依据的那些决策。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          最好的产品管理软件不是某一款——而是一套小栈,再加上一个让底下决策不漂移的东西。
        </blockquote>
        <cite>留着你的路线图、跟踪器、文档、PRD 写作器。补上那一层,把它们攥到一起。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '最好的产品管理软件是哪个？', a: '看用途。路线图用 Productboard 或 Aha，跟踪工作用 Linear 或 Jira，文档用 Notion 或 Coda，用 AI 起草 spec 用 ChatPRD 或 Notion AI——再加一层像 Draftlize 这样的决策层，让底下的决策不漂移。没有单一工具能把这些都做好；最强的团队跑的是一套精心搭的小栈。' },
            { q: '产品管理软件和项目管理软件有什么区别？', a: '产品管理软件帮你决定做什么、为什么做——路线图、反馈、spec、决策。项目管理软件帮你跟踪这些工作的执行——任务、冲刺、截止日期。Jira、Linear 偏后者，Productboard、Aha 偏前者。多数团队两类各用一个。' },
            { q: '我需要专门的 PM 软件，还是 Jira 或 Notion 就够了？', a: '对小团队来说，Jira 加一个 Notion wiki 其实能覆盖很多。当反馈量、路线图相关方或决策漂移长到一个 wiki 装不下时，专门工具才值回票价。真正的失效点不是缺软件——而是一套常见工具栈里，没有哪个能让底下的决策保持最新，于是它们悄悄失去同步。' },
            { q: '最好的免费产品管理软件是哪个？', a: 'Notion、Linear、Jira 都有能用的免费档，Trello 做简单看板则完全免费。Draftlize 也免费起步：注册送 $5，按量付费、用多少算多少，没有订阅，余额永不过期。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">试试这层"决策层",<em>注册送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>按量付费,用多少算多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            路线图、跟踪、文档、spec,继续用你已经在用的软件。补上 Draftlize,只为了那件它们都不做的事:让底下的决策一直是对的。
          </p>
          <a className="btn-primary" href="/">注册送 $5,开始使用</a>
        </div>
      </section>
    </MarketingShell>
  );
}
